/*
 * AlarmIndicatorRenderer — kompaktes Alarm-Status-Pill fuer die HMI. Bindet
 * NICHT gegen den generischen IPointBindingSource sondern gegen den globalen
 * `activeAlarmsSignal` (SignalR-getrieben, siehe realtime/alarmsStore.ts) —
 * das ist die eine Aggregation die HMI-tenant-weit gilt und die man nicht
 * pro Leaf abonnieren will.
 *
 * Filter-Semantik:
 *   - kein Filter gesetzt: alle Alarme im aktuellen Data-Scope
 *   - scopedToPlant: alle Alarme des aktuellen Plants (Plant-Id kommt aus
 *     der Route und wird per URL-Match ausgewertet; ist die HMI nicht auf
 *     einer plant-Route, faellt der Filter durch)
 *   - pointFilter: nur Alarme dieses einen Leaf-ids
 */

import type { AlarmIndicatorWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { activeAlarmsSignal } from '../../realtime/alarmsStore';
import type { AlarmDto } from '../../api/types';
import './AlarmIndicatorRenderer.css';

interface Props {
  widget: AlarmIndicatorWidget;
  // Kept for the renderer-interface uniformity even though this renderer
  // doesn't consume a value from it. Alarms flow through the SignalR-backed
  // alarmsStore instead.
  source: IPointBindingSource;
}

export function AlarmIndicatorRenderer({ widget }: Props) {
  const all = activeAlarmsSignal.value;
  const filtered = filterAlarms(all, widget);

  const activeCount = filtered.filter((a) => a.state === 'Active').length;
  const highest = highestSeverity(filtered);
  const tone = highest === null ? 'ok' : highest === 'Alarm' ? 'alarm' : 'warn';

  const label = widget.label ?? defaultLabel(widget);

  return (
    <div
      class={`hv-alarm-ind hv-alarm-ind--${tone}`}
      role="status"
      aria-label={`${label}: ${describeState(tone, activeCount)}`}
      title={buildTitle(filtered, activeCount)}
    >
      <span class={`hv-alarm-ind__dot hv-alarm-ind__dot--${tone}`} aria-hidden="true" />
      <span class="hv-alarm-ind__label">{label}</span>
      {widget.showCount !== false && (
        <span class={`hv-alarm-ind__count hv-alarm-ind__count--${tone}`}>
          {activeCount}
        </span>
      )}
    </div>
  );
}

function filterAlarms(list: AlarmDto[], widget: AlarmIndicatorWidget): AlarmDto[] {
  if (widget.pointFilter) {
    return list.filter((a) => a.leafId === widget.pointFilter);
  }
  if (widget.scopedToPlant) {
    // Plant-Filter aus der URL ableiten: die Devices-Route ist plant-scoped
    // und traegt die plantId als /plants/<guid>/... im Pathname. Kein plant
    // in der URL → kein Filter (aggregiert ueber alles im Data-Scope).
    const plantId = extractPlantIdFromLocation();
    if (plantId) return list.filter((a) => a.plantId === plantId);
  }
  return list;
}

function extractPlantIdFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(
    /\/plants\/([0-9a-fA-F-]{36})\b/,
  );
  return match?.[1] ?? null;
}

function highestSeverity(list: AlarmDto[]): 'Warning' | 'Alarm' | null {
  let seenWarn = false;
  for (const a of list) {
    if (a.state !== 'Active') continue;
    if (a.severity === 'Alarm') return 'Alarm';
    if (a.severity === 'Warning') seenWarn = true;
  }
  return seenWarn ? 'Warning' : null;
}

function describeState(tone: string, count: number): string {
  if (tone === 'alarm') return `${count} aktive Alarme`;
  if (tone === 'warn') return `${count} aktive Warnungen`;
  return 'keine aktiven Alarme';
}

function buildTitle(list: AlarmDto[], activeCount: number): string {
  if (activeCount === 0) return 'Keine aktiven Alarme';
  const active = list.filter((a) => a.state === 'Active').slice(0, 5);
  const lines = active.map((a) => `${a.severity}: ${a.message}`);
  if (activeCount > 5) lines.push(`… und ${activeCount - 5} weitere`);
  return lines.join('\n');
}

function defaultLabel(widget: AlarmIndicatorWidget): string {
  if (widget.pointFilter) return 'Alarm';
  if (widget.scopedToPlant) return 'Anlagen-Alarme';
  return 'Alarme';
}
